import { BeneficialOwnerControlType } from '@commenda/api';
import { useEffect, useState } from 'react';

interface UseUboControlTypeSyncArgs {
  hsc: boolean;
  aboveThreshold: boolean;
  // Seed from existing data (edit form). Omit for create forms.
  initial?: BeneficialOwnerControlType | null;
}

interface UseUboControlTypeSyncResult {
  controlType: BeneficialOwnerControlType | null;
  setControlType: (next: BeneficialOwnerControlType | null) => void;
  error: string | undefined;
  clearError: () => void;
  // Returns false when the field is required-but-empty and surfaces the
  // inline error. Caller short-circuits its submit.
  validate: (requireWhen: boolean) => boolean;
}

// Auto-fill: HSC on + above threshold + empty ⇒ DIRECT_OWNERSHIP; HSC off ⇒
// clear. Manual picks survive because the auto-fill predicate requires the
// state to still be null.
export function useUboControlTypeSync({
  hsc,
  aboveThreshold,
  initial,
}: UseUboControlTypeSyncArgs): UseUboControlTypeSyncResult {
  const [controlType, setControlType] = useState<BeneficialOwnerControlType | null>(
    initial ?? null,
  );
  const [error, setError] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (hsc && aboveThreshold && controlType == null) {
      setControlType(BeneficialOwnerControlType.DIRECT_OWNERSHIP);
    } else if (!hsc && controlType != null) {
      setControlType(null);
    }
  }, [hsc, aboveThreshold, controlType]);

  const validate = (requireWhen: boolean): boolean => {
    const next = requireWhen && controlType == null ? 'Select a control type.' : undefined;
    setError(next);
    return next == null;
  };

  return {
    controlType,
    setControlType,
    error,
    clearError: () => setError(undefined),
    validate,
  };
}
